'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { LabelDefault, MetaDefault } from '../../../../components/typography';
import { ReadOnlyCodeEditor } from '../../components/ReadOnlyCodeEditor';

const MCP_TOOL_CALL_VIEWER_NAMES = new Set([
  'neo-mcp-tools-call',
  'neo-mcp-prompts-get',
  'neo-mcp-resources-read',
  'ai-discovery-mcp',
]);

const BOUND_MCP_ATTACK_TOOL_NAME = /^mcp_[0-9a-f]{32}$/i;

const GENERIC_MCP_FAILURE_PREFIX = 'The MCP tool call failed.';
const OBSERVED_METADATA_MARKER = '\n\n--- observed metadata ---';

export function isMcpToolCallViewerTool(toolName: string): boolean {
  const normalized = toolName.replace(/_/g, '-').toLowerCase();

  return (
    MCP_TOOL_CALL_VIEWER_NAMES.has(normalized) ||
    BOUND_MCP_ATTACK_TOOL_NAME.test(toolName)
  );
}

type McpToolCallOutputBodyProps = {
  toolName: string;
  args?: Record<string, unknown>;
  output?: string;
  error?: string;
};

type McpToolCallView =
  | { kind: 'response'; text: string }
  | { kind: 'error'; text: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJson(value: string): unknown | undefined {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function stripObservedMetadata(text: string): string {
  const index = text.indexOf(OBSERVED_METADATA_MARKER);

  return index === -1 ? text : text.slice(0, index).trim();
}

function parseToolOutput(value: string): unknown {
  const trimmed = stripObservedMetadata(value).trim();
  const direct = parseJson(trimmed);

  if (direct !== undefined) {
    return direct;
  }

  const jsonStart = trimmed.indexOf('{');

  if (jsonStart === -1) {
    return trimmed;
  }

  return parseJson(trimmed.slice(jsonStart)) ?? trimmed;
}

function unwrapLangChainMessage(value: unknown): unknown {
  if (typeof value === 'string') {
    const parsed = parseJson(value);

    return parsed !== undefined ? unwrapLangChainMessage(parsed) : value;
  }

  if (!isRecord(value)) {
    return value;
  }

  if (
    value.lc === 1 &&
    isRecord(value.kwargs) &&
    value.kwargs.content !== undefined
  ) {
    return unwrapLangChainMessage(value.kwargs.content);
  }

  return value;
}

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function formatStructuredContent(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed.length > 0 ? trimmed : undefined;
  }

  return prettyJson(value);
}

function readStructuredContent(
  record: Record<string, unknown>,
): string | undefined {
  const topLevel = formatStructuredContent(record.structuredContent);

  if (topLevel) {
    return topLevel;
  }

  if (!Array.isArray(record.content)) {
    return undefined;
  }

  for (const block of record.content) {
    if (!isRecord(block)) {
      continue;
    }

    const nested = formatStructuredContent(block.structuredContent);

    if (nested) {
      return nested;
    }
  }

  return undefined;
}

function readContentValue(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (!Array.isArray(content)) {
    return '';
  }

  return content
    .map((block) => {
      if (typeof block === 'string') {
        return block;
      }

      if (!isRecord(block)) {
        return '';
      }

      if (typeof block.text === 'string') {
        return block.text;
      }

      const resource = isRecord(block.resource) ? block.resource : undefined;

      return typeof resource?.text === 'string' ? resource.text : '';
    })
    .filter((part) => part.length > 0)
    .join('\n');
}

type McpContentPayload = {
  content: unknown;
  isError: boolean;
};

function readMcpContentPayload(value: unknown): McpContentPayload | undefined {
  const record = unwrapLangChainMessage(value);

  if (!isRecord(record)) {
    return undefined;
  }

  if (record.content !== undefined) {
    const structured = readStructuredContent(record);

    return {
      content: structured ?? record.content,
      isError: record.isError === true,
    };
  }

  if (typeof record.text === 'string' && record.type === 'text') {
    const structured = readStructuredContent(record);

    return {
      content: structured ?? record.text,
      isError: record.isError === true,
    };
  }

  const structured = formatStructuredContent(record.structuredContent);

  if (structured) {
    return {
      content: structured,
      isError: record.isError === true,
    };
  }

  const response = isRecord(record.response) ? record.response : undefined;

  if (!response) {
    return undefined;
  }

  const responseError =
    typeof response.error === 'string' ? response.error.trim() : '';

  if (responseError) {
    return { content: responseError, isError: true };
  }

  const result = response.result;

  if (typeof result === 'string' && result.trim()) {
    const nested = readMcpContentPayload(
      parseJson(result.trim()) ?? result.trim(),
    );

    return nested ?? { content: result.trim(), isError: false };
  }

  if (result !== undefined && result !== null) {
    return readMcpContentPayload(result);
  }

  return undefined;
}

function resolveTraceError({
  output,
  error,
}: {
  output?: string;
  error?: string;
}): string | undefined {
  const errorText = error?.trim() ?? '';
  const outputText = output?.trim() ?? '';

  if (errorText && !errorText.startsWith(GENERIC_MCP_FAILURE_PREFIX)) {
    return errorText;
  }

  if (outputText.startsWith(GENERIC_MCP_FAILURE_PREFIX) && errorText) {
    return errorText;
  }

  if (
    errorText &&
    outputText &&
    errorText === outputText &&
    !outputText.startsWith(GENERIC_MCP_FAILURE_PREFIX)
  ) {
    return errorText;
  }

  return undefined;
}

function normalizeMcpViewerToolName(toolName: string): string {
  return toolName.replace(/_/g, '-').toLowerCase();
}

function formatMcpToolCallArgsForDisplay({
  toolName,
  args,
}: {
  toolName: string;
  args?: Record<string, unknown>;
}): string | undefined {
  if (!args || Object.keys(args).length === 0) {
    return undefined;
  }

  const normalized = normalizeMcpViewerToolName(toolName);

  if (
    normalized === 'neo-mcp-tools-call' ||
    normalized === 'ai-discovery-mcp'
  ) {
    return JSON.stringify(
      {
        tool: args.toolName,
        arguments: args.arguments ?? {},
      },
      null,
      2,
    );
  }

  if (normalized === 'neo-mcp-prompts-get') {
    return JSON.stringify(
      {
        prompt: args.promptName,
        arguments: args.promptArguments ?? {},
      },
      null,
      2,
    );
  }

  if (normalized === 'neo-mcp-resources-read') {
    return JSON.stringify({ uri: args.resourceUri }, null, 2);
  }

  return JSON.stringify(args, null, 2);
}

function inferReadOnlyCodeLanguage(text: string): 'json' | 'text' {
  const trimmed = text.trim();

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return 'text';
  }

  try {
    JSON.parse(trimmed);

    return 'json';
  } catch {
    return 'text';
  }
}

function extractMcpToolCallView({
  output,
  error,
}: {
  output?: string;
  error?: string;
}): McpToolCallView | null {
  const traceError = resolveTraceError({ output, error });

  if (traceError) {
    return { kind: 'error', text: traceError };
  }

  const outputText = output?.trim() ?? '';

  if (!outputText) {
    return null;
  }

  const payload = readMcpContentPayload(parseToolOutput(outputText));

  if (payload) {
    const text = readContentValue(payload.content).trim();

    if (!text) {
      return null;
    }

    return {
      kind: payload.isError ? 'error' : 'response',
      text,
    };
  }

  if (outputText.startsWith(GENERIC_MCP_FAILURE_PREFIX)) {
    return { kind: 'error', text: outputText };
  }

  return { kind: 'response', text: stripObservedMetadata(outputText) };
}

type McpToolCallSectionProps = {
  label: string;
  value: string;
  isError?: boolean;
};

function McpToolCallSection({
  label,
  value,
  isError = false,
}: McpToolCallSectionProps) {
  const language = inferReadOnlyCodeLanguage(value);

  return (
    <Box
      width="100%"
      borderWidth="1px"
      borderColor={isError ? 'border.error' : 'border.neutral.muted'}
      borderRadius="md"
      overflow="hidden"
      bg="transparent"
    >
      <Box
        px={2}
        py={1}
        borderBottomWidth="1px"
        borderBottomColor={isError ? 'border.error' : 'border.neutral.muted'}
      >
        <LabelDefault color={isError ? 'fg.error' : 'fg.muted'}>
          {label}
        </LabelDefault>
      </Box>
      <Box width="100%" maxH="240px" overflow="auto">
        {isError ? (
          <Box px={2} py={2}>
            <MetaDefault color="fg.error">{value}</MetaDefault>
          </Box>
        ) : (
          <ReadOnlyCodeEditor
            value={value}
            language={language}
            maxHeight="240px"
            borderless
            transparent
            showLineNumbers={language === 'json'}
          />
        )}
      </Box>
    </Box>
  );
}

export function McpToolCallOutputBody({
  toolName,
  args,
  output,
  error,
}: McpToolCallOutputBodyProps) {
  const t = useTranslations('designSystem.chat');
  const argsText = formatMcpToolCallArgsForDisplay({ toolName, args });
  const view = extractMcpToolCallView({ output, error });
  const outputText = view?.text.trim() ?? '';

  if (!argsText && !outputText) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {argsText ? (
        <McpToolCallSection label={t('input')} value={argsText} />
      ) : null}
      {outputText ? (
        <McpToolCallSection
          label={t('output')}
          value={outputText}
          isError={view?.kind === 'error'}
        />
      ) : null}
    </VStack>
  );
}
